/**
 * The account's tweets under a line item, what is already attached, and the two writes
 * that change it: Promote and Remove — the Ads panel of the line item's own screen (PR 3 of
 * audits/2026-10-29/gui/plan.md).
 *
 * 🔴 Attaching creates a promoted tweet, and X creates it **ACTIVE** with no PUT to
 * switch it off — so the only way back is Remove, which deletes it. The console attaches
 * only while the line item is PAUSED, and that rule is **ours, not X's**:
 * `lib/creative-copy.ts` carries the wording, including the window the pre-write read
 * narrows and cannot close.
 *
 * 🔴 After a successful attach this screen asks its parent to re-read the line item
 * (`onPromoted`). The status it checked against is from before the write; if the line
 * item was switched on in between, the operator must see that rather than our stale
 * reading.
 *
 * Both tweet switches are explicit. X's own default timeline is NULLCAST, which is
 * promoted-only tweets, so a screen that sent nothing would answer "which of our
 * tweets can we promote" with an ads-only list that looks complete.
 */
import { type FormEvent, useEffect, useState } from "react";
import {
	type LineItem,
	type PromotedTweet,
	type Tweet,
	UnauthorizedError,
	type XAccount,
	api,
} from "../lib/api.ts";
import {
	ALREADY_ATTACHED,
	ATTACHING,
	LOOKUP,
	MORE_THAN_SHOWN,
	ORDER,
	REMOVING,
	TIMELINES,
	TIMELINE_MEANS,
	TYPES,
	WRITES,
	approvalNote,
	deliveringNote,
	droppedNote,
	loadedLabel,
	promotedLabel,
	tweetLabel,
} from "../lib/creative-copy.ts";
import { ADS_PANEL, ATTACHING_FOLD } from "../lib/line-item-screen-copy.ts";
import { Badge, Button, Fold, InfoBanner, Input, Panel, Select, Table } from "../ui/index.ts";

interface Props {
	lineItem: LineItem;
	account: XAccount;
	/**
	 * 🔴 Asked to re-read the line item after a successful attach. The parent owns that
	 * row, and the status this screen checked against was read before the write.
	 */
	onPromoted: () => void;
}

export function Creative({ lineItem, account, onPromoted }: Props) {
	const [type, setType] = useState<string>("");
	const [timeline, setTimeline] = useState<string>("");
	const [rows, setRows] = useState<Tweet[] | null>(null);
	/**
	 * 🔴 The typed id of "Find by id". A QUERY to X and nothing else: it is sent through
	 * `api.tweetsById`, the rows that come back are X's, and what the attach sends is
	 * `chosen` — written only by the Select over those rows. `promote()` never reads it.
	 */
	const [lookup, setLookup] = useState<string>("");
	/** Which read the rows on screen came from, so the words under them say so. */
	const [readBy, setReadBy] = useState<"page" | "id" | null>(null);
	const [dropped, setDropped] = useState(0);
	const [more, setMore] = useState(false);
	const [busy, setBusy] = useState(false);
	const [failed, setFailed] = useState<string | null>(null);
	const [attached, setAttached] = useState<PromotedTweet[] | null>(null);
	const [attachedFailed, setAttachedFailed] = useState<string | null>(null);
	const [chosen, setChosen] = useState<string>("");
	const [writing, setWriting] = useState(false);
	const [wrote, setWrote] = useState<string | null>(null);
	const [writeFailed, setWriteFailed] = useState<string | null>(null);

	/**
	 * What is attached now. Read on open and after every write, because the list is the
	 * only place a removal becomes visible: deleted rows are not asked for, so a removed
	 * promoted tweet simply stops being listed.
	 */
	const readAttached = () => {
		api
			.promotedTweets(account.xAccountId, lineItem.id)
			.then((page) => {
				setAttached(page.promotedTweets);
				setAttachedFailed(null);
			})
			.catch((e) => {
				if (e instanceof UnauthorizedError) return;
				setAttached(null);
				setAttachedFailed(e instanceof Error ? e.message : String(e));
			});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: the line item and the account ARE the subject — this effect must re-run when either changes, and `readAttached` closes over exactly those two
	useEffect(() => {
		setAttached(null);
		setWrote(null);
		setWriteFailed(null);
		readAttached();
	}, [lineItem.id, account.xAccountId]);

	const delivering = lineItem.entityStatus !== "PAUSED";
	// What X lists on this line item, not deleted: not offered again (the worker refuses it too).
	const attachedTweetIds = new Set(
		(attached ?? []).flatMap((p) => (p.deleted || p.tweetId === null ? [] : [p.tweetId])),
	);

	/**
	 * Attach the chosen tweet.
	 *
	 * 🔴 Refused here as well as disabled in the UI, and refused for BOTH reasons: no
	 * tweet chosen, and a line item that is not PAUSED. A disabled button is a
	 * suggestion; the handler is the rule — and the worker refuses it again with 409,
	 * which is the rule that counts.
	 */
	async function promote() {
		if (!chosen || delivering || writing || attachedTweetIds.has(chosen)) return;
		setWriting(true);
		setWrote(null);
		setWriteFailed(null);
		try {
			const res = await api.promote({
				account: account.xAccountId,
				lineItemId: lineItem.id,
				tweetIds: [chosen],
			});
			const body = (res.body ?? {}) as { error?: string; created?: unknown; ids?: string[] };
			if (res.status === 201) {
				setWrote(`Attached ${body.ids?.join(", ") ?? chosen}.`);
				setChosen("");
				readAttached();
				// 🔴 The parent re-reads the line item: our PAUSED reading predates the write.
				onPromoted();
				return;
			}
			setWriteFailed(body.error ?? `X refused the attach (${res.status}).`);
			// A 502 can carry `created: true` — the promoted tweet may exist, so the list
			// and the line item are both re-read rather than left as they were.
			if (body.created !== undefined) {
				readAttached();
				onPromoted();
			}
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			setWriteFailed(e instanceof Error ? e.message : String(e));
		} finally {
			setWriting(false);
		}
	}

	/**
	 * Remove one, after a confirmation that says what cannot be undone.
	 *
	 * `window.confirm` here rather than an in-page second click: the sentence is long
	 * and the browser's own dialog cannot be dismissed by a stray click on the page.
	 */
	async function remove(id: string) {
		if (writing) return;
		if (!window.confirm(REMOVING.confirm(id))) return;
		setWriting(true);
		setWrote(null);
		setWriteFailed(null);
		try {
			const res = await api.removePromotedTweet(id, account.xAccountId);
			const body = (res.body ?? {}) as { error?: string; deleted?: unknown };
			if (res.status === 200 && body.deleted === true) {
				setWrote(REMOVING.gone(id));
				readAttached();
				return;
			}
			if (body.deleted === "unknown") {
				setWriteFailed(REMOVING.unknown(id));
				readAttached();
				return;
			}
			setWriteFailed(body.error ?? `X refused the removal (${res.status}).`);
			readAttached();
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			setWriteFailed(e instanceof Error ? e.message : String(e));
		} finally {
			setWriting(false);
		}
	}

	async function read(e: FormEvent) {
		e.preventDefault();
		// 🔴 Neither switch is defaulted, so neither can be left to X: the button stays
		// unavailable until the operator has said which set of tweets they mean.
		if (!type || !timeline || busy) return;
		setBusy(true);
		setFailed(null);
		try {
			const page = await api.tweets(account.xAccountId, type, timeline);
			setRows(page.tweets);
			// 🔴 A new list, an empty choice: `chosen` is what promote() sends, and a value
			// left over from the previous read is a row that is no longer on screen — the
			// Select shows the first option while the attach would send the old id (d6, #147).
			setChosen("");
			setReadBy("page");
			setDropped(page.droppedWithoutIdStr);
			setMore(page.truncated);
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setRows(null);
			setFailed(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	/**
	 * The read by id, since 2026-10-12: the list form answered nothing for the one tweet
	 * this account has, while X returns it by `tweet_ids` (docs/START-HERE.md, «Список
	 * твитов пуст — твит читается только по id»). The digits typed are the question;
	 * the rows set here are X's answer and land in the SAME list the Select reads.
	 */
	async function findById(e: FormEvent) {
		e.preventDefault();
		const id = lookup.trim();
		// Digits only, and a type: X reads `tweet_ids` under a `tweet_type`, and an id that
		// is not digits names nothing. Refused here as well as disabled below.
		if (!type || !/^[0-9]+$/.test(id) || busy) return;
		setBusy(true);
		setFailed(null);
		try {
			const page = await api.tweetsById(account.xAccountId, type, [id]);
			setRows(page.tweets);
			// 🔴 Same as above, and the read that made it likely: find B by id after
			// choosing A from a page, and without this line promote() would send A.
			setChosen("");
			setReadBy("id");
			setDropped(page.droppedWithoutIdStr);
			setMore(page.truncated);
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setRows(null);
			setFailed(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	const note = droppedNote(dropped);

	return (
		<Panel
			title={ADS_PANEL}
			aside={<span className="font-mono text-xs text-muted">{lineItem.id}</span>}
		>
			<p className="mt-1 text-sm text-muted">{ORDER}</p>

			{/*
			 * 🔴 The banner stays open — it is what a Promote makes, said before the press — and
			 * so does the window the pre-write read cannot close: a risk named on purpose (e1's
			 * requirement) is not folded away. Only the decisions and whose act a start is fold;
			 * they are explanations (`Fold`), not a state X reports.
			 */}
			<InfoBanner variant="warning" title={ATTACHING.title}>
				{ATTACHING.body}
			</InfoBanner>
			<Fold summary={ATTACHING_FOLD}>
				<ul className="grid gap-1">
					{ATTACHING.decided.map((d) => (
						<li key={d}>• {d}</li>
					))}
				</ul>
				<p className="mt-1">{ATTACHING.whose}</p>
			</Fold>
			<p className="text-sm text-muted">{ATTACHING.window}</p>
			<p className="text-sm text-muted">{WRITES}</p>

			<h4 className="mt-4 text-sm font-semibold text-content">Attached now</h4>
			{attachedFailed && (
				<InfoBanner variant="danger" title="X's promoted tweets could not be read.">
					{attachedFailed}
				</InfoBanner>
			)}
			{wrote && (
				<InfoBanner variant="success" title="Done.">
					{wrote}
				</InfoBanner>
			)}
			{writeFailed && (
				<InfoBanner variant="danger" title="X did not do what was asked.">
					{writeFailed}
				</InfoBanner>
			)}
			{attached !== null && (
				<Table<PromotedTweet>
					columns={[
						{
							id: "what",
							header: "Promoted tweet",
							accessor: (p) => <span className="text-xs text-content">{promotedLabel(p)}</span>,
						},
						{
							id: "id",
							header: "",
							accessor: (p) => <span className="font-mono text-xs text-muted">{p.id}</span>,
						},
						{
							id: "approval",
							header: "X's review",
							accessor: (p) => (
								<span className="text-xs text-muted">{approvalNote(p.approvalStatus)}</span>
							),
						},
						{
							id: "remove",
							header: "",
							accessor: (p) => (
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={writing}
									onClick={() => {
										void remove(p.id);
									}}
								>
									Remove
								</Button>
							),
						},
					]}
					data={attached}
					keyExtractor={(p) => p.id}
					emptyMessage="X lists no promoted tweets on this line item."
				/>
			)}

			<form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={read}>
				<div className="w-48">
					<Select
						label="Which tweets"
						value={type}
						onChange={(e) => setType(e.target.value)}
						placeholder="Choose a type"
						options={TYPES.map((t) => ({ value: t, label: t }))}
					/>
				</div>
				<div className="w-56">
					<Select
						label="Which timeline"
						value={timeline}
						onChange={(e) => setTimeline(e.target.value)}
						placeholder="Choose a timeline"
						options={TIMELINES.map((t) => ({ value: t, label: t }))}
					/>
				</div>
				<Button
					type="submit"
					variant="outline"
					size="sm"
					loading={busy}
					disabled={!type || !timeline}
				>
					List tweets
				</Button>
			</form>
			{timeline && (
				<p className="mt-1 text-sm text-muted">
					{TIMELINE_MEANS[timeline as keyof typeof TIMELINE_MEANS]}
				</p>
			)}

			<form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={findById}>
				<div className="w-72">
					<Input
						label={LOOKUP.label}
						inputMode="numeric"
						value={lookup}
						onChange={(e) => setLookup(e.target.value)}
						helperText={LOOKUP.helper}
					/>
				</div>
				<Button
					type="submit"
					variant="outline"
					size="sm"
					loading={busy}
					disabled={!type || !/^[0-9]+$/.test(lookup.trim())}
				>
					{LOOKUP.label}
				</Button>
			</form>

			{failed && (
				<InfoBanner variant="danger" title="X's tweets could not be read.">
					{failed}
				</InfoBanner>
			)}
			{note && (
				<InfoBanner variant="warning" title="Some tweets could not be listed.">
					{note}
				</InfoBanner>
			)}

			{rows !== null && (
				<>
					<Table<Tweet>
						columns={[
							{
								id: "what",
								header: "Tweet",
								accessor: (t) => <span className="text-xs text-content">{tweetLabel(t)}</span>,
							},
							{
								id: "id",
								header: "",
								accessor: (t) => <span className="font-mono text-xs text-muted">{t.id}</span>,
							},
							{
								id: "created",
								header: "Created",
								accessor: (t) => (
									<span className="font-mono text-xs text-muted">{t.createdAt ?? "—"}</span>
								),
							},
						]}
						data={rows}
						keyExtractor={(t) => t.id}
						loading={busy}
						emptyMessage={
							readBy === "id" ? LOOKUP.empty : "X returned no tweets for that type and timeline."
						}
					/>
					<div className="mt-2 flex flex-wrap items-center gap-3">
						<span className="text-sm text-muted">{loadedLabel(rows.length, more)}</span>
						{more && <Badge variant="warning">{MORE_THAN_SHOWN}</Badge>}
					</div>
					{readBy === "id" && <p className="mt-1 text-sm text-muted">{LOOKUP.shown}</p>}
					<div className="mt-3 flex flex-wrap items-end gap-3">
						<div className="w-72">
							<Select
								label="Promote which tweet"
								value={chosen}
								onChange={(e) => setChosen(e.target.value)}
								placeholder="Choose a tweet"
								options={rows.map((t) => ({
									value: t.id,
									label: attachedTweetIds.has(t.id)
										? `${tweetLabel(t)} · ${t.id} · ${ALREADY_ATTACHED.suffix}`
										: `${tweetLabel(t)} · ${t.id}`,
									disabled: attachedTweetIds.has(t.id),
								}))}
							/>
						</div>
						<Button
							type="button"
							variant="primary"
							size="sm"
							loading={writing}
							disabled={!chosen || delivering}
							onClick={() => {
								void promote();
							}}
						>
							Promote
						</Button>
						{delivering && (
							<span className="text-sm text-muted">{deliveringNote(lineItem.entityStatus)}</span>
						)}
					</div>
					{rows.some((t) => attachedTweetIds.has(t.id)) && (
						<p className="text-xs text-muted">{ALREADY_ATTACHED.note}</p>
					)}
				</>
			)}
		</Panel>
	);
}
